/**
 * Optimizer run output → the cards the Performance tabs render.
 *
 * Built in the FRONTEND, and on top of resolveOptimizationSuggestions, for the
 * reason optimizerDigest.js's header spells out: each platform's optimizer emits
 * a different suggestion shape, there is exactly ONE resolver that knows all of
 * them, and a surface that re-derived its own set would show the user a
 * different list than the agent and the apply path act on. This module maps —
 * it does not re-resolve.
 *
 * The field names below are the contract in
 * campaign_optimizer/gads_optimizer_launcher_base.py (EVIDENCE_CONTRACT) and
 * campaign_optimizer/headline_guards.py:
 *
 *   headline                           the CARD TITLE. <= 55 chars, opens with a
 *                                      verb. MAY BE NULL — headline_guards emits
 *                                      no headline rather than a bad one, and the
 *                                      card then falls back to plain_summary and
 *                                      says so.
 *   consequence                        what stays true if nobody acts, <= 8 words
 *   plain_summary                      the FULL sentence — detail view, and only
 *                                      the title when there is no headline
 *   why.evidence / why.mechanism       the two halves of "why this"
 *   cost_of_inaction                   "if nothing changes", over 30 days
 *   scoring.expected_lift              metric, direction, point_pct, range_*
 *   scoring.confidence                 0-100, capped at 40 on thin data, never 100
 *   current_value / recommended_value  the change itself
 *   target_type / target_id            the entity it applies to
 *
 * TWO SHAPES, both real. The launcher emits the nested form above; the normalized
 * card digest (docs/fixtures/performance-agent-cards-2026-08-27.json) flattens it
 * to `evidence` / `mechanism` / `confidence` / `priority_score` / `lift_*`. Every
 * read below accepts either, because a card built from the wrong one does not
 * error — it renders blank fields and reads as an analysis that said nothing.
 *
 * Anything absent stays null and the tab renders the absence, rather than a
 * plausible-looking substitute. A card that shows an invented number is worse
 * than a card that says the analysis did not produce one.
 */

import { resolveOptimizationSuggestions } from './optimizationSuggestions';
import { isUnsupportedInterestRateFinding } from './interestRateFindings';
import { isInactiveDiagnosticCampaign } from './diagnosticCampaignEligibility';
import { diagnosticEntities, findingOnlyConcernsInactiveEntities } from './inactiveDiagnosticFindings';
import {
  googleRecommendationLabel, googleRecommendationGroupLabel, isMaintenanceRecommendationType,
  verifiedCustomerMatchNames, googleRecommendationGroupSummary,
} from './googleRecommendationTypes';

/** analysis_kind (messages.sub_action) → the tab that shows its cards. */
export const ROUTE_BY_ANALYSIS = {
  diagnostic: 'diagnostics',
  pacing: 'pacing',
  performance: 'recommendations',
};

/**
 * route → the tab name a reader sees.
 *
 * 'hidden' is deliberately absent: it is not a tab. Turn-on cards live behind a
 * checkbox on whichever tab owns them, so naming it in a sentence about where to
 * go next would send the reader somewhere that does not exist.
 */
export const ROUTE_LABEL = {
  diagnostics: 'Diagnostics',
  pacing: 'Budget & pacing',
  recommendations: 'Recommendations',
};

/** analysis_kind → the agent label the card is attributed to. */
export const AGENT_BY_ANALYSIS = {
  diagnostic: 'Diagnostic',
  pacing: 'Pacing',
  performance: 'Performance',
};

/**
 * Anything → a string React can render, or null.
 *
 * EXPORTED because the crash it prevents does not only happen to mapped cards.
 * A performance run also carries `suppressed`, `experiments` and
 * `googleReview`, and one of those fields —
 * `google_projection` — arrived as Google's raw impact delta, a dict of
 * {impressions, clicks, cost_micros, conversions, conversions_value,
 * video_views}. React threw error #31 and the whole Recommendations tab went
 * white. The backend now renders that field, but runs already stored carry the
 * object, so the render side has to hold too.
 */
export const asText = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) {
    return v
      .map((x) => (x && typeof x === 'object' ? (x.keyword || JSON.stringify(x)) : String(x)))
      .join(', ') || null;
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const asNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** `why` is an object per the contract, but older runs sent a bare string. */
function readWhy(s) {
  const why = s.why;
  if (why && typeof why === 'object' && !Array.isArray(why)) {
    return { ev: asText(why.evidence), me: asText(why.mechanism) };
  }
  // A string `why` is a mechanism-shaped sentence with no quoted figures behind
  // it. Report it as the mechanism and leave evidence empty rather than
  // promoting prose into an evidence slot the reader trusts for numbers.
  return { ev: asText(s.evidence), me: asText(why) || asText(s.mechanism || s.rationale || s.reasoning || s.description) };
}

/**
 * "If nothing changes", which arrives in two shapes.
 *
 * growth_guards.enforce_growth_evidence writes cost_of_inaction as an OBJECT —
 * {summary, quantified_30d, reversible} — and fills a placeholder one when the
 * analysis omitted it. Older/other launchers emit a plain string. Read as a
 * string, the object stringifies to raw JSON on the card face.
 *
 * The quantified 30-day figure is preferred because that is what the row asks
 * for; the summary is the fallback, and a guard-filled placeholder still says
 * something true ("skipping this leaves the campaign performing as it does
 * today") so it is shown rather than suppressed.
 */
function readCostOfInaction(s) {
  const coi = s.cost_of_inaction;
  if (coi && typeof coi === 'object' && !Array.isArray(coi)) {
    return asText(coi.quantified_30d) || asText(coi.summary) || null;
  }
  return asText(coi);
}

/** scoring.expected_lift → the five lift fields the card row renders. */
function readLift(s) {
  // expected_gain is the growth runs' own field and takes precedence: it is what
  // EVIDENCE_CONTRACT requires, and the guard derives it from
  // scoring.expected_lift when the analysis omitted it — so when both exist they
  // agree, and when only one does it is usually this one.
  const gain = s.expected_gain;
  if (gain?.status === 'unknown' && (gain.scope === 'demandgen' || s.impact_status === 'unverified')) {
    return { lm: 'Impact unknown — test required', ld: null, lp: null, ll: null, lh: null };
  }
  if (gain && typeof gain === 'object' && !Array.isArray(gain)) {
    // Two forms. Percentages when the guard derived it from scoring.expected_lift;
    // ABSOLUTE monthly figures when the analysis supplied them directly, which the
    // contract asks for ("the change in ABSOLUTE units over a month — not only a
    // percentage"). An absolute is not a percentage and must not be rendered with
    // a % sign, so it is carried as the metric label instead.
    if (gain.point_pct != null) {
      return {
        lm: asText(gain.metric),
        ld: asText(gain.direction),
        lp: asNum(gain.point_pct),
        ll: asNum(gain.range_low_pct),
        lh: asNum(gain.range_high_pct),
      };
    }
    if (gain.absolute_per_month != null) {
      const range = (gain.range_low_per_month != null && gain.range_high_per_month != null)
        ? ` (${gain.range_low_per_month}–${gain.range_high_per_month})` : '';
      return {
        lm: `${gain.absolute_per_month}${range} ${asText(gain.metric) || ''} a month`.trim(),
        ld: asText(gain.direction),
        lp: null, ll: null, lh: null,
      };
    }
  }

  const lift = s.scoring?.expected_lift || s.expected_lift || null;
  if (!lift || typeof lift !== 'object') {
    // The digest's flat form. Checked before falling through to expected_impact
    // so a card carrying real percentages is never demoted to a HIGH/LOW band.
    if (s.lift_metric != null || s.lift_point != null) {
      return {
        lm: asText(s.lift_metric),
        ld: asText(s.lift_direction),
        lp: asNum(s.lift_point),
        ll: asNum(s.lift_low),
        lh: asNum(s.lift_high),
      };
    }
    // expected_impact is the older HIGH/MEDIUM/LOW field the DV360 and Facebook
    // launchers still emit. It is a band, not a percentage — carry it as the
    // metric label so the card can say so instead of rendering a fake number.
    return { lm: asText(s.expected_impact), ld: null, lp: null, ll: null, lh: null };
  }
  return {
    lm: asText(lift.metric),
    ld: asText(lift.direction),
    lp: asNum(lift.point_pct),
    ll: asNum(lift.range_low_pct),
    lh: asNum(lift.range_high_pct),
  };
}

/**
 * Values that mean "switch this on".
 *
 * Turning an entity on is a build decision, not an optimisation — a campaign
 * that has not started yet is PENDING, not failing — so these are routed to
 * 'hidden' and shown only when the reader asks for them.
 */
const TURN_ON = /^(enabled?|active|unpause|resume|turn on)$/i;

function isTurnOn(s) {
  const action = String(s.action_type || s.type || '').toLowerCase();
  if (action !== 'status') return false;
  const rec = String(asText(s.recommended_value ?? s.suggested_values) || '').trim();
  return TURN_ON.test(rec);
}

/**
 * Whether this suggestion may carry a one-click Apply button.
 *
 * `auto_applicable` is stamped by the backend's own value-and-instruction gate
 * (campaign_optimizer/suggestion_applicability.py), which exists because an
 * advisory suggestion dressed in an executable action type once rendered an
 * Apply button that set a daily budget to INR 14. This reads that stamp; it does
 * not re-derive it. `advisory` is the DV360 marker from the resolver.
 */
const canApply = (s) => s.auto_applicable === true && s.advisory !== true;

/**
 * The platform ids a suggestion acts on, grouped by kind, for display.
 *
 * Shown on the card so a change can be found in the platform after it is
 * applied — a line item's name is not unique in DV360 and can be renamed, its
 * id cannot. The focused DV360 analyses name targets as `line_item_id` /
 * `line_item_ids` and `insertion_order_id(s)`, the TTD ones as `ad_group_id`;
 * a `target_id` counts when its `target_type` is one of those kinds. Other
 * targets (a Google Ads campaign or asset group) stay unlabelled: the type and
 * name already say which thing that is, and planners find them by name.
 *
 * @returns {Array<{label: string, ids: string[]}>} empty when there are none
 */
const ID_KINDS = [
  { label: 'Line item', keys: ['line_item_id', 'line_item_ids'], types: ['line_item', 'lineitem', 'line item'] },
  { label: 'Insertion order', keys: ['insertion_order_id', 'insertion_order_ids'], types: ['insertion_order', 'insertion order', 'io'] },
  { label: 'Ad group', keys: ['ad_group_id', 'ad_group_ids', 'adgroup_id'], types: ['ad_group', 'adgroup', 'ad group'] },
];
const idList = (value) => (Array.isArray(value) ? value : [value])
  .map((id) => (typeof id === 'number' ? String(id) : typeof id === 'string' ? id.trim() : ''))
  .filter((id) => id && !id.includes(':') && !/\s/.test(id));
export function entityIds(s) {
  if (!s || typeof s !== 'object') return [];
  const targetType = String(s.target_type || s.entity_type || '').trim().toLowerCase();
  const targetIds = idList(s.target_id);
  return ID_KINDS.map(({ label, keys, types }) => {
    const ids = keys.flatMap((key) => idList(s[key]));
    if (types.includes(targetType)) ids.push(...targetIds);
    return { label, ids: [...new Set(ids)] };
  }).filter((group) => group.ids.length);
}

/**
 * One run → its cards.
 *
 * @param {object|null} runResult  a message json_response from an optimize-campaign run
 * @param {object} meta { analysisKind, camp, campName, dest, pcid, messageId }
 * @returns {Array<object>} cards, in the optimizer's own priority order
 */
export function cardsFromRun(runResult, {
  analysisKind, camp, campName, dest, pcid, messageId, recurrence, appliedEdits = null, currency = null,
} = {}) {
  const details = runResult?.details || null;
  if (analysisKind === 'diagnostic' && (details?.analysis?.skipped
    || isInactiveDiagnosticCampaign(details?.analysis || {},
      runResult?.analysis_data?.campaign_structure || {}))) return [];
  // The corridor's own currency. IDFC runs eight of them and the brief is
  // explicit that spend is never comparable across corridors without saying so,
  // which makes a bare number worse than no number.
  const ccy = [runResult?.analysis_inputs?.currency_code,
    runResult?.analysis_data?.campaign_structure?.currency_code,
    details?.analysis?.campaign_pacing?.currency_code,
    runResult?.currency_code, currency].find(value => typeof value === 'string' && /^[A-Z]{3}$/.test(value)) || null;
  const kind = String(analysisKind || '').toLowerCase();
  const agent = AGENT_BY_ANALYSIS[kind] || 'Performance';
  const baseRoute = ROUTE_BY_ANALYSIS[kind] || 'recommendations';

  return resolveOptimizationSuggestions(details).map((s, index) => {
    const { ev, me } = readWhy(s);
    return {
      // The card's identity IN THE FEED, which spans the account. Scoped by
      // campaign because `${kind}-${index}` alone is only unique within one run:
      // every campaign's diagnostic run produces a `diagnostic-0`, so two
      // campaigns' first findings collided. That id keys the optimistic-dismiss
      // map, so dismissing campaign A's first finding also removed campaign B's
      // — and handed B's card A's dismissal record, showing the wrong reason,
      // the wrong scope, and a Reinstate button bound to A's dismissal. React
      // logged duplicate keys for the same reason.
      //
      // `index` is untouched: it is the position in the RUN's own array, which
      // is what the apply path records as suggestionIndex.
      id: `${camp || 'acct'}-${kind}-${index}`,
      index,
      // The run this suggestion came from. Together with `index` it is the
      // (optimizeMessageId, suggestionIndex) pair every existing apply path tags
      // its edit with, and what makes "already applied" survive a reload.
      messageId: messageId || null,
      // The untouched suggestion. buildSuggestionEditRequest reads fields this
      // card shape does not carry (target_entities, suggested_values,
      // dv360_edit_operation), and re-deriving an edit from the flattened card
      // would be a second, divergent apply path — the exact thing
      // optimizerSuggestionApply.js exists to prevent.
      raw: s,
      route: isTurnOn(s) ? 'hidden' : baseRoute,
      camp: camp || null,
      campName: campName || null,
      // The PLATFORM this card's campaign runs on, carried per card rather than
      // read off the page. buildSuggestionEditRequest shapes the edit per
      // platform, and the feed spans the account — there is no single page-level
      // destination to fall back on once more than one campaign is in view.
      dest: dest || null,
      // The platform's own campaign id, resolved by the caller. `camp` names the
      // row in our database; this names the campaign at the DSP, and the two are
      // not interchangeable — the edit route is given both.
      pcid: pcid || null,
      agent,
      cat: asText(s.category) || null,
      action: asText(s.action_type || s.type || s.lever) || null,
      tt: asText(s.target_type || s.entity_type) || null,
      tn: asText(s.target_name || s.entity_name) || null,
      tid: asText(s.target_id) || null,
      // Platform ids to show beside the recommendation (entityIds).
      eids: entityIds(s),
      // The headline is the title. NOT plain_summary: that averages ~177 chars
      // and is the detail view's opening sentence. When headline_guards wrote
      // none, `head` stays null and RecCard falls back to plain_summary AND
      // labels it as a fallback — the reader is told the title is borrowed.
      head: asText(s.headline) || null,
      plain: asText(s.plain_summary) || asText(s.title) || null,
      // One line on what stays true if nobody acts. Distinct from cost_of_inaction,
      // which is the quantified 30-day version in the body.
      cons: asText(s.consequence) || null,
      ctx: asText(s.consequence) || asText(s.description) || null,
      ev,
      me,
      coi: readCostOfInaction(s),
      cur: asText(s.current_value),
      rec: asText(s.recommended_value ?? s.suggested_values),
      // Two different things, and the design uses both: `pri` is
      // scoring.priority_score (0-100) and is what the list is ORDERED by —
      // "the list is ordered by priority_score, highest first", per the
      // contract, and the executable payloads are applied in that order.
      // `priBand` is the suggestion's own HIGH/MEDIUM/LOW, which is what the
      // badge shows. Collapsing them would either lose the ordering or invent a
      // band from a score.
      // What this finding puts at risk, in the campaign's own currency. The
      // agent computes it on every card and it never reached the reader: the
      // AUS logo card carries 293,867 and its headline says "Add assets to 10
      // asset groups". A card that names a number is read; one that names a
      // chore is scrolled past, and these five outscored every other card in
      // the account at priority 95.
      sar: asNum(s.spend_at_risk),
      ccy,
      // Every entity an AGGREGATE card covers. One card for ten asset groups is
      // the right shape — it is one fix — but without the ids the page cannot
      // tell that this card and the ten diagnosis rows under it are the same
      // finding, so it reports it eleven times. Empty on a single-entity card,
      // where target_id already says it.
      tids: Array.isArray(s.target_ids)
        ? s.target_ids.map((x) => String(x)).filter(Boolean)
        : [],
      pri: asNum(s.scoring?.priority_score ?? s.priority_score),
      priBand: s.priority ? String(s.priority).toLowerCase() : null,
      conf: asNum(s.scoring?.confidence ?? s.confidence),
      ...readLift(s),
      auto: canApply(s),
      // Why a real idea has no button. The reader is owed the reason, not just
      // the absence — every advisory card on the design names it.
      advisoryReason: canApply(s) ? null : (asText(s.advisory_reason) || null),
      judge: asText(s._judge_verdict?.verdict ?? s._judge_verdict) || null,
      judgeReason: asText(s._judge_verdict?.reason) || null,
      suppressedReason: asText(s._suppressed_reason) || null,
      // What the PREVIOUS run of this kind said about the same finding —
      // computed server-side (routes/v2/performance.js recurrenceFor) from the
      // stored runs and keyed by this run's own suggestion index, so no key
      // function has to be mirrored here. Null means first sighting or no
      // prior run, and renders as nothing. previous_value goes through asText
      // like every rendered value: a budget_shift's object value must render
      // as text, not as [object Object].
      recur: (recurrence && recurrence[index])
        ? { ...recurrence[index], previous_value: asText(recurrence[index].previous_value) }
        : null,
      // Stamped in code by growth_guards.annotate_already_applied (Run
      // Continuity, Phase 2) when this suggestion repeats a change a person
      // already applied — key AND value matched. Annotate-only upstream; here
      // it becomes a chip and a detail row, never a demotion.
      applied: s._already_applied
        ? { at: s._already_applied.at || null, value: asText(s._already_applied.value) }
        : null,
      // The Apply button's own record, read at request time by /runs from the
      // edit tagged (optimizeMessageId, suggestionIndex): applying | applied |
      // failed, when, and by whom. Distinct from `applied` above, which the
      // optimizer stamps only on its NEXT run. Moves the card into the Applied
      // section unless the attempt failed — see partitionApplied.
      appliedEdit: (appliedEdits && appliedEdits[index]) ? appliedEdits[index] : null,
      // Cross-agent flags, filled in by annotateContention over the whole set.
      conflict: false,
      dupe: false,
      n: 1,
      nlev: 1,
      agents: [],
      elsewhere: [],
    };
  }).filter(card => !isUnsupportedInterestRateFinding(card.raw)
    && (analysisKind !== 'diagnostic' || !findingOnlyConcernsInactiveEntities(
      card.raw, diagnosticEntities(details?.analysis))));
}

/**
 * Fold a performance run's test plan and Google review into its normal cards.
 *
 * A Google review row is evidence, not an edit instruction. When it points at
 * `our_suggestion_index`, the corresponding normal suggestion already carries
 * the backend-validated edit payload and remains the single actionable card;
 * this function only labels its source. An unlinked Google row cannot safely be
 * applied even when its mapped lever is generally executable, because it has no
 * target/value operation of its own. Tests likewise describe an experiment and
 * decision rule, not an immediate platform mutation. Both therefore enter the
 * same feed as Advisory cards rather than living in separate sections below it.
 */
export function mergePerformanceRecommendationCards(cards = [], {
  experiments = [], googleReview = [], googleRecommendations = [], camp = null, campName = null,
} = {}) {
  const verifiedListNames = verifiedCustomerMatchNames(googleRecommendations);
  const merged = cards.map((card) => ({ ...card }));
  const primaryByIndex = new Map(
    merged.filter((card) => card.route === 'recommendations')
      .map((card) => [card.index, card]),
  );

  for (const review of googleReview || []) {
    if (!review || typeof review !== 'object') continue;
    // Reviews remain in the audit data; only accepted recommendations enter the work queue.
    if (!['adopt', 'adapt'].includes((asText(review.verdict) || '').toLowerCase())) continue;
    const rawIndex = review.our_suggestion_index;
    const linkedIndex = rawIndex === null || rawIndex === undefined || rawIndex === ''
      ? null : Number(rawIndex);
    const linked = Number.isInteger(linkedIndex) ? primaryByIndex.get(linkedIndex) : null;
    if (linked) {
      linked.origin = 'Google';
      linked.originDetail = asText(review.verdict) || null;
      continue;
    }

    const type = asText(review.type) || 'Google recommendation';
    const googleTarget = asText(review.target);
    const target = googleTarget || campName || 'this campaign';
    const verdict = asText(review.verdict);
    const reasoning = asText(review.reasoning);
    const projection = asText(review.google_projection);
    const advisoryReason = review.lever_is_executable
      ? 'No validated edit payload was produced for this Google recommendation.'
      : verdict === 'unreviewed'
        ? 'This Google recommendation was not reviewed into a validated platform edit.'
        : 'This recommendation is evidence only and has no validated platform edit.';
    const index = 20000 + merged.length;
    merged.push({
      id: `${camp || 'acct'}-performance-google-${index}`,
      index,
      messageId: null,
      raw: null,
      route: 'recommendations',
      camp,
      campName,
      dest: null,
      pcid: null,
      agent: 'Performance',
      origin: 'Google',
      originDetail: verdict,
      cat: 'google-recommendation',
      action: asText(review.mapped_lever) || type,
      tt: 'google recommendation',
      tn: target,
      tid: `${type}:${target}`,
      // The enum is Google's identifier, not a sentence. It stays in `tid` and
      // `googleType`, where dismissals and grouping key on it.
      head: googleRecommendationLabel(type),
      plain: reasoning,
      cons: projection,
      ctx: projection || reasoning,
      ev: projection,
      me: reasoning,
      coi: null,
      cur: null,
      rec: null,
      sar: null,
      ccy: null,
      tids: [],
      pri: null,
      priBand: null,
      conf: null,
      lm: null,
      ld: null,
      lp: null,
      ll: null,
      lh: null,
      auto: false,
      advisoryReason,
      // Google's own identity for the recommendation, and where it points.
      // An account-level row (target "account", no campaign ids) is the SAME
      // recommendation in every campaign's run that carried it — these are
      // what collapseAccountGoogleRecommendations dedupes on.
      googleType: type,
      googleListName: type === 'REFRESH_CUSTOMER_MATCH_LIST' ? verifiedListNames.get(asText(review.recommendation_id)) || null : null,
      recommendationId: asText(review.recommendation_id),
      googleTarget,
      googleCampaignIds: Array.isArray(review.campaign_ids) ? review.campaign_ids.map(String) : [],
      maintenance: isMaintenanceRecommendationType(type),
      judge: verdict,
      judgeReason: reasoning,
      suppressedReason: null,
      recur: null,
      applied: null,
      appliedEdit: null,
      conflict: false,
      dupe: false,
      n: 1,
      nlev: 1,
      agents: [],
      elsewhere: [],
    });
  }

  for (const experiment of experiments || []) {
    if (!experiment || typeof experiment !== 'object') continue;
    const test = asText(experiment.test) || 'Proposed test';
    const measure = asText(experiment.measure);
    const duration = asNum(experiment.duration_days);
    const hypothesis = asText(experiment.hypothesis);
    const decisionRule = asText(experiment.decision_rule);
    const context = [
      duration !== null ? `${duration}-day test` : null,
      measure ? `measures ${measure}` : null,
    ].filter(Boolean).join(' · ') || 'Experiment proposed by the performance analysis';
    const index = 10000 + merged.length;
    merged.push({
      id: `${camp || 'acct'}-performance-test-${index}`,
      index,
      messageId: null,
      raw: null,
      route: 'recommendations',
      camp,
      campName,
      dest: null,
      pcid: null,
      agent: 'Performance',
      origin: 'Test',
      originDetail: null,
      cat: 'experiment',
      action: 'test',
      tt: 'experiment',
      tn: test,
      tid: test,
      head: test,
      plain: hypothesis ? `Hypothesis: ${hypothesis}` : null,
      cons: context,
      ctx: context,
      ev: hypothesis,
      me: decisionRule,
      coi: null,
      cur: null,
      rec: null,
      sar: null,
      ccy: null,
      tids: [],
      pri: null,
      priBand: null,
      conf: null,
      lm: null,
      ld: null,
      lp: null,
      ll: null,
      lh: null,
      auto: false,
      advisoryReason: 'This is a measured experiment with a decision rule, not an immediate platform edit.',
      judge: null,
      judgeReason: null,
      suppressedReason: null,
      recur: null,
      applied: null,
      appliedEdit: null,
      conflict: false,
      dupe: false,
      n: 1,
      nlev: 1,
      agents: [],
      elsewhere: [],
    });
  }

  return merged;
}

/** An unlinked Google card whose recommendation belongs to the account, not a campaign. */
export const isAccountGoogleCard = (card) => Boolean(card) && card.origin === 'Google'
  && card.cat === 'google-recommendation' && !card.raw
  && String(card.googleTarget || '').toLowerCase() === 'account'
  && !(card.googleCampaignIds || []).length;

/**
 * One card per account-level Google recommendation TYPE, across every run.
 *
 * Google returns account-level recommendations (target "account", no campaign
 * ids) — refresh a customer-match list, say — to every campaign's analysis,
 * because the fetch is per customer. mergePerformanceRecommendationCards runs
 * once per campaign, so each one became a card per campaign: on IDFC (7 Oct)
 * 26 list refreshes × 4 PMax/AI-Max campaigns were 104 of the feed's 172 cards.
 *
 * Two steps, both keyed on Google's own identity rather than on text:
 *
 *   dedupe    the same recommendation_id from several runs is ONE item, which
 *             remembers every campaign whose run carried it
 *   collapse  items of one type become ONE card with a count and the items
 *             underneath ("Refresh 26 customer-match lists")
 *
 * The card is account-scoped: `camp` is null — it belongs to no one campaign —
 * and `affectedCampaigns` lists the campaigns whose runs raised it, which is
 * what the campaign filter matches on (cardInCampaign). Campaign-scoped Google
 * cards are untouched: the same type on two campaigns is two decisions.
 *
 * Without a Google ID, retain each source row: identical wording does not prove
 * that two recommendations refer to the same list or resource.
 * Returns a new array; account groups take the position of their first card.
 */
export function collapseAccountGoogleRecommendations(cards = []) {
  const groups = new Map(); // googleType → { first, items: Map(itemKey → item), verdicts, sources }
  const out = [];
  for (const card of cards.flatMap((card) => card.googleSourceCards || [card])) {
    if (!isAccountGoogleCard(card)) { out.push(card); continue; }
    const type = card.googleType || card.tid;
    if (!groups.has(type)) {
      groups.set(type, { first: card, items: new Map(), campaigns: new Map(), sources: [] });
      out.push({ __group: type });
    }
    const group = groups.get(type);
    group.sources.push(card);
    const itemKey = card.recommendationId || card;
    if (!group.items.has(itemKey)) {
      group.items.set(itemKey, {
        recommendationId: card.recommendationId || null,
        listName: card.googleListName || null,
        verdict: card.originDetail || null,
        reasoning: card.plain || null,
        projection: card.cons || null,
        campaigns: [],
      });
    } else {
      // The same account recommendation can receive different campaign reviews.
      // Keep every distinct explanation and verdict instead of picking the first.
      const item = group.items.get(itemKey);
      const peers = group.sources.filter((source) => source.recommendationId === card.recommendationId);
      const words = (key, separator) => [...new Set(peers.map((source) => source[key]).filter(Boolean))].join(separator) || null;
      item.reasoning = words('plain', '\n');
      item.projection = words('cons', '\n');
      item.verdict = words('originDetail', ' / ');
      if (card.googleListName && !item.listNameConflict) {
        if (item.listName && item.listName !== card.googleListName) {
          item.listName = null;
          item.listNameConflict = true;
        } else item.listName = card.googleListName;
      }
    }
    if (card.camp) {
      const item = group.items.get(itemKey);
      if (!item.campaigns.includes(card.camp)) item.campaigns.push(card.camp);
      if (!group.campaigns.has(card.camp)) group.campaigns.set(card.camp, card.campName || null);
    }
  }
  return out.map((entry) => {
    if (!entry || !entry.__group) return entry;
    const { first, items, campaigns, sources } = groups.get(entry.__group);
    const list = [...items.values()];
    const n = list.length;
    const affectedCampaigns = [...campaigns.keys()];
    const verdicts = [...new Set(list.map((item) => item.verdict).filter(Boolean))];
    const only = n === 1 ? list[0] : null;
    const summary = googleRecommendationGroupSummary(first.googleType, list);
    const raisedIn = affectedCampaigns.length === 1
      ? `Raised in 1 campaign's analysis`
      : `Raised in ${affectedCampaigns.length} campaigns' analyses`;
    return {
      ...first,
      id: `acct-performance-google-${String(entry.__group).toLowerCase()}`,
      camp: null,
      campaignId: null, // Keep dismissals account-scoped even on a campaign page.
      campName: 'Account-wide',
      dest: null,
      pcid: null,
      messageId: null,
      head: googleRecommendationGroupLabel(first.googleType, n),
      originDetail: verdicts.join(' / ') || first.originDetail,
      judge: verdicts.join(' / ') || first.judge,
      // One item: its own words. Several: the items carry their own, below.
      plain: only ? only.reasoning : summary,
      cons: summary || (only ? only.projection : raisedIn),
      ctx: only ? (only.projection || only.reasoning) : raisedIn,
      ev: only ? only.projection : null,
      me: only ? only.reasoning : null,
      judgeReason: only ? only.reasoning : null,
      recommendationId: only ? only.recommendationId : null,
      count: n,
      googleItems: list,
      googleListName: only ? only.listName : null,
      googleSourceCards: sources,
      affectedCampaigns,
      affectedCampaignNames: affectedCampaigns.map((camp) => campaigns.get(camp) || camp),
    };
  });
}

/**
 * Whether a card belongs under one campaign in the campaign filter: its own
 * campaign, or — for an account-scoped card with no campaign of its own — any
 * campaign whose analysis raised it.
 */
export function cardInCampaign(card, key) {
  if (!card) return false;
  if (card.camp === key) return true;
  return !card.camp && Array.isArray(card.affectedCampaigns) && card.affectedCampaigns.includes(key);
}

/** Cards from every run in a set, e.g. the three focused analyses for a campaign. */
export function cardsFromRuns(runs = []) {
  return runs.flatMap((r) => cardsFromRun(r.result, {
    analysisKind: r.analysisKind,
    camp: r.camp,
    campName: r.campName,
    dest: r.dest,
    pcid: r.pcid,
    messageId: r.messageId,
  }));
}

// Scoped by CAMPAIGN, for the same reason the card id is: the feed spans the
// account, and a target key without it collides. A platform entity id is
// globally unique so it is safe alone, but the fallback is `target_name` — and
// a multi-entity card's name is a count label like "3 Asset Groups", which
// repeats across campaigns by construction. Unscoped, two campaigns each
// proposing asset work on three of their own groups read as one finding
// duplicated, and both get a "the Performance agent also weighed in" banner
// pointing at a card about a different campaign's asset groups.
const targetKey = (c) => `${c.camp || ''}::${c.tt || ''}::${c.tid || c.tn || ''}`;

/**
 * Mark the cards that touch the same entity as another card.
 *
 * Two agents reaching the same target is the single most dangerous thing this
 * feed can hide: the Diagnostic agent asks for creative on an asset group the
 * Performance agent wants paused, and applying either alone is defensible while
 * applying both in sequence is not. So contention is computed across the WHOLE
 * set — every route at once — and each card carries where else its target is
 * spoken about.
 *
 *   dupe     same target, same action — applying once is enough
 *   conflict same target, different actions — read them together first
 *
 * Mutates and returns the array.
 */
export function annotateContention(cards = []) {
  const byTarget = new Map();
  cards.forEach((c) => {
    if (!c.tt && !c.tid && !c.tn) return; // campaign-wide with no entity: nothing to contend over
    const k = targetKey(c);
    if (!byTarget.has(k)) byTarget.set(k, []);
    byTarget.get(k).push(c);
  });

  byTarget.forEach((group) => {
    if (group.length < 2) return;
    const levers = new Set(group.map((c) => c.action).filter(Boolean));
    group.forEach((c) => {
      const others = group.filter((o) => o !== c);
      c.n = group.length;
      c.nlev = levers.size;
      c.agents = [...new Set(others.map((o) => o.agent).filter((a) => a && a !== c.agent))];
      // The TAB NAMES a reader sees, not the route slugs. The banner read
      // "also weighed in, on pacing" — a key from the router — and could name
      // "hidden", which is not a tab at all but the turn-on set that lives
      // behind a checkbox on each tab.
      c.elsewhere = [...new Set(others.map((o) => o.route).filter((r) => r && r !== c.route))]
        .map((r) => ROUTE_LABEL[r])
        .filter(Boolean);
      // Same lever on the same target is a duplicate; different levers contend.
      c.dupe = levers.size === 1;
      c.conflict = levers.size > 1;
    });
  });

  return cards;
}

/**
 * The optimizer's own order: priority_score, highest first.
 *
 * A card with no score sorts last rather than as zero — "the analysis did not
 * score this" and "the analysis scored this zero" are different claims, and only
 * the second one earns a place at the bottom of a ranked list on merit. Ties
 * keep the run's original order, which is already the order the applies dispatch
 * in. Returns a new array.
 */
export function sortByPriority(cards = []) {
  return [...cards].sort((a, b) => {
    // Housekeeping (list refreshes, ad-strength nudges) sorts below every
    // substantive card, scored or not. Still in the feed — just not first.
    if (Boolean(a.maintenance) !== Boolean(b.maintenance)) return a.maintenance ? 1 : -1;
    if (a.pri === b.pri) return a.index - b.index;
    if (a.pri === null) return 1;
    if (b.pri === null) return -1;
    return b.pri - a.pri;
  });
}


/**
 * The stable identity of a FINDING — the twin of `findingKey` in
 * apps/app-backend/utils/performanceDismissals.js.
 *
 * Duplicated across the two packages the way accountSettingPlatformList already
 * is: there is no shared module between app-backend and app-frontend, and cards
 * are BUILT here, so the match has to happen here too. Both sides are tested on
 * the same shapes; change one, change the other.
 *
 * Keyed on agent + action + target, never on the card id or suggestion index —
 * those are positions in one run's array and are renumbered by the next run, so
 * a dismissal keyed on either would let the finding reappear.
 */
export function findingKey(card = {}) {
  return [
    String(card.agent || '').toLowerCase(),
    String(card.action || '').toLowerCase(),
    String(card.tt || '').toLowerCase(),
    String(card.tid || card.tn || '').toLowerCase(),
  ].join('|');
}

/** Whether one active dismissal covers this card. Mirrors the server's rule. */
export function dismissalCovers(dismissal, card) {
  if (!dismissal || !card) return false;
  if (['finding', 'campaign_check'].includes(dismissal.scope) && dismissal.campaign_id != null
      && String(dismissal.campaign_id) !== String(card.camp)) return false;
  const sameCheck = String(dismissal.agent || '').toLowerCase() === String(card.agent || '').toLowerCase()
    && String(dismissal.action_type || '').toLowerCase() === String(card.action || '').toLowerCase();
  switch (dismissal.scope) {
    case 'finding': return dismissal.finding_key === findingKey(card);
    case 'campaign_check':
    case 'account_check':
    case 'all_campaigns_check': return sameCheck;
    // An unknown scope suppresses nothing. Failing open would hide findings
    // nobody dismissed.
    default: return false;
  }
}

/**
 * Split cards into what still shows and what a dismissal covers.
 *
 * The dismissed half keeps its dismissal so the Dismissed section can say what
 * it was dismissed FOR — a dismissal without its reason is just a disappearance.
 */
export function partitionDismissed(cards = [], dismissals = []) {
  if (!dismissals.length) return { live: cards, dismissed: [] };
  const live = [];
  const dismissed = [];
  for (const card of cards) {
    const hit = dismissals.find((d) => dismissalCovers(d, card));
    if (hit) dismissed.push({ card, dismissal: hit });
    else if (card.googleSourceCards?.length) {
      // Legacy campaign-scoped dismissals still apply to their original rows.
      // Rebuild from the surviving sources so counts and campaign filters stay true.
      const sources = partitionDismissed(card.googleSourceCards, dismissals);
      live.push(...collapseAccountGoogleRecommendations(sources.live));
      dismissed.push(...sources.dismissed);
    } else live.push(card);
  }
  return { live, dismissed };
}

/**
 * Split cards into what is still to do and what the Apply button already did.
 *
 * A failed attempt stays live: the card still carries the change, and the
 * failure is shown on it so the reader can try again.
 */
export function partitionApplied(cards = []) {
  const live = [];
  const applied = [];
  for (const card of cards) {
    if (card.appliedEdit && card.appliedEdit.status !== 'failed') applied.push(card);
    else live.push(card);
  }
  return { live, applied };
}

// Collapse only identical actions with identical targets and proposed changes.
// Keep the chosen card's original message/index/raw payload for Apply and retain
// the other sources for evidence. Conflicting values remain separate cards.
export function consolidateRecommendations(input = []) {
  // Account-level Google recommendations first: they repeat across runs by
  // construction and are deduped on Google's id, not on the keys below.
  const cards = collapseAccountGoogleRecommendations(input);
  const stable = value => {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])]));
    return value ?? null;
  };
  const groups = new Map();
  for (const card of cards) {
    const targets = card.tids?.length ? [...card.tids].sort() : card.tid;
    const raw = card.raw || {};
    const accountFinding = raw._account_review_scope;
    const accountKey = accountFinding?.customer_id && accountFinding?.start && accountFinding?.end
      && raw.recommended_value?.review_type && raw.recommended_value?.findings && card.auto === false
      ? JSON.stringify(['account-review', stable(accountFinding), stable(raw.recommended_value)]) : null;
    const funnelKey = raw._funnel_review && card.auto === false && card.camp && raw.recommended_value?.findings
      ? JSON.stringify(['funnel-review', card.camp, raw._funnel_review, stable(raw.recommended_value)]) : null;
    const key = accountKey || funnelKey || (card.camp && card.action && targets && card.route !== 'hidden' && card.raw && !card.advisoryReason
      ? JSON.stringify([card.camp, card.tt, targets, card.action, stable(raw.recommended_value),
        stable(raw.suggested_values), stable(raw.target_entities), stable(raw.sem_edit_operation),
        stable(raw.dv360_edit_operation), stable(raw.ttd_edit_operation),
        stable(raw.current_value), stable(raw.operation), stable(raw.scope)])
      : card.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(card);
  }
  return [...groups.values()].map(group => {
    const rank = card => card.appliedEdit && card.appliedEdit.status !== 'failed' ? 0 : card.auto ? 1 : card.route === 'diagnostics' ? 2 : 3;
    const chosen = [...group].sort((a, b) => rank(a) - rank(b))[0];
    return group.length === 1 ? chosen : { ...chosen,
      affectedCampaigns: [...new Set(group.map(card => card.camp).filter(Boolean))],
      alsoReportedBy: group.filter(card => card !== chosen).map(card => ({
        agent: card.agent, head: card.head, plain: card.plain, messageId: card.messageId,
        index: card.index, evidence: card.ev,
      })),
    };
  });
}

/** Cards for one tab, with contention computed across every tab first. */
export function cardsForRoute(cards = [], route) {
  return cards.filter((c) => c.route === route);
}

/** The 'hidden' turn-on cards belonging to a tab, for its "show turn-on" toggle. */
export function hiddenCardsForRoute(cards = [], route) {
  const agentOf = { diagnostics: 'Diagnostic', pacing: 'Pacing' };
  const agent = agentOf[route] || 'Performance';
  return cards.filter((c) => c.route === 'hidden' && c.agent === agent);
}

const performanceCards = {
  ROUTE_BY_ANALYSIS,
  AGENT_BY_ANALYSIS,
  cardsFromRun,
  mergePerformanceRecommendationCards,
  cardsFromRuns,
  annotateContention,
  sortByPriority,
  collapseAccountGoogleRecommendations,
  cardInCampaign,
  findingKey,
  dismissalCovers,
  partitionDismissed,
  partitionApplied,
  cardsForRoute,
  hiddenCardsForRoute,
};

export default performanceCards;
